<template>
  <div class="pos-container">
    <!-- Fixed Cart Sidebar (Left) -->
    <div class="cart-sidebar-fixed">
      <div class="cart-sidebar-header">
        <h3>
          <i class="bi bi-cart"></i>
          Panier
        </h3>
        <span class="cart-badge">{{ cartItems.length }}</span>
      </div>

      <div class="cart-sidebar-content">
        <div
          v-for="(cartItem, index) in cartItems"
          :key="index"
          class="cart-sidebar-item"
        >
          <div class="cart-sidebar-item-image">
            <img
              v-if="getOrderItemImage(cartItem)"
              :src="$imageUrl(getOrderItemImage(cartItem))"
              :alt="getOrderItemName(cartItem)"
              @error="handleImageError($event)"
            />
            <div v-else class="item-thumb-placeholder">
              <i class="bi bi-image"></i>
            </div>
          </div>
          <div class="cart-sidebar-item-details">
            <h5 class="cart-sidebar-item-name">
              {{ getOrderItemName(cartItem) }}
            </h5>
            <div class="cart-sidebar-item-meta">
              <span class="cart-sidebar-qty"
                >x{{ cartItem.quantity || 0 }}</span
              >
              <span class="cart-sidebar-price">
                {{ formatPrice(cartItem.totalPriceTTC || cartItem.total || 0) }}
                MAD
              </span>
            </div>
            <div class="cart-sidebar-item-actions">
              <div class="cart-sidebar-quantity-controls">
                <button
                  class="qty-btn-sm minus"
                  @click="decreaseCartItemQuantity(cartItem)"
                >
                  <i class="bi bi-dash"></i>
                </button>
                <span class="qty-value-sm">{{ cartItem.quantity || 0 }}</span>
                <button
                  class="qty-btn-sm plus"
                  @click="increaseCartItemQuantity(cartItem)"
                >
                  <i class="bi bi-plus"></i>
                </button>
              </div>
              <button
                class="remove-btn-sm"
                @click="removeItemFromCart(cartItem)"
                title="Retirer"
              >
                <i class="bi bi-trash"></i>
              </button>
            </div>
          </div>
        </div>

        <div v-if="cartItems.length === 0" class="empty-cart-sidebar">
          <i class="bi bi-cart-x"></i>
          <p>Panier vide</p>
        </div>
      </div>

      <div class="cart-sidebar-footer">
        <div class="cart-sidebar-total">
          <span>Total:</span>
          <strong>{{ formatPrice(totalPayment) }} MAD</strong>
        </div>
      </div>
    </div>

    <!-- Main Content Area (Right) -->
    <div class="pos-main-content">
      <!-- Search and Filter Section -->
      <div class="search-filter-section">
        <!-- Search Mode Selection -->
        <div class="search-mode-selector">
          <button
            type="button"
            class="search-mode-btn"
            :class="{ active: searchMode === 'barcode' }"
            @click="setSearchMode('barcode')"
          >
            <i class="bi bi-upc-scan"></i>
            Code-barres
          </button>
          <button
            type="button"
            class="search-mode-btn"
            :class="{ active: searchMode === 'code' }"
            @click="setSearchMode('code')"
          >
            <i class="bi bi-search"></i>
            Code / Nom
          </button>
        </div>

        <!-- Barcode Search -->
        <form
          v-if="searchMode === 'barcode'"
          class="search-form"
          @submit.prevent="searchAndAddItem"
        >
          <div class="search-box">
            <i class="bi bi-upc-scan"></i>
            <input
              ref="barcodeInput"
              v-model="barcodeSearch"
              type="text"
              placeholder="Scanner ou saisir un code-barres..."
              autofocus
              @input="onBarcodeInput"
            />
          </div>
          <button
            type="submit"
            class="search-submit-btn"
            :disabled="!barcodeSearch || searchingItem"
          >
            <i v-if="!searchingItem" class="bi bi-search"></i>
            <span v-else class="spinner-small"></span>
            Rechercher
          </button>
        </form>

        <!-- Search by Code or Name -->
        <div v-if="searchMode === 'code'" class="search-form">
          <div class="search-box">
            <i class="bi bi-search"></i>
            <input
              ref="itemSearchInput"
              v-model="itemSearchTerm"
              type="text"
              placeholder="Rechercher par code article ou nom..."
              @input="onItemSearchInput"
              @keydown.enter.prevent="addFirstSearchResult"
            />
            <span v-if="searchingItems" class="spinner-small"></span>
          </div>
        </div>
      </div>

      <!-- Search Results -->
      <div
        v-if="visibleItemSearchResults.length > 0"
        class="search-results-section"
      >
        <h4 class="search-results-title">
          Résultats de recherche ({{ itemSearchResults.length }})
        </h4>
        <div class="search-results-grid">
          <div
            v-for="item in visibleItemSearchResults"
            :key="item.reference || item.id"
            class="search-result-card"
            @click="addItemFromSearch(item)"
          >
            <div class="result-image-wrapper">
              <img
                v-if="getItemImage(item)"
                :src="$imageUrl(getItemImage(item))"
                :alt="item.fullName || item.name"
                @error="handleImageError($event)"
              />
              <div v-else class="item-image-placeholder">
                <i class="bi bi-image"></i>
              </div>
            </div>
            <div class="result-info">
              <h5 class="result-name">
                {{ item.fullName || item.name || "Article" }}
              </h5>
              <p class="result-code" v-if="item.reference">
                Code: {{ item.code || item.reference }}
              </p>
              <p class="result-stock" v-if="getItemStock(item) !== null">
                Stock: {{ formatStock(getItemStock(item)) }}
              </p>
              <div class="result-price">
                {{ formatPrice(getItemPrice(item)) }} MAD
              </div>
            </div>
            <div class="result-action">
              <button class="add-item-btn">
                <i class="bi bi-plus-circle"></i>
                Ajouter
              </button>
            </div>
          </div>
        </div>
      </div>

      <!-- Empty Search State -->
      <div
        v-if="
          itemSearchTerm && !searchingItems && itemSearchResults.length === 0
        "
        class="empty-search-state"
      >
        <i class="bi bi-search"></i>
        <p>Aucun article trouvé pour "{{ itemSearchTerm }}"</p>
      </div>
    </div>

    <!-- Right Sidebar - Order Summary -->
    <div class="pos-sidebar">
      <div class="order-header">
        <h3>Commandes en cours</h3>
        <span class="order-id">#{{ currentOrderId }}</span>
      </div>

      <!-- Client Selection -->
      <div class="customer-selection">
        <label>Client *</label>
        <button
          type="button"
          class="customer-select-btn"
          :class="{ 'has-selection': selectedCustomer }"
          @click="showCustomerModal = true"
        >
          <span v-if="selectedCustomer">
            <i class="bi bi-person"></i>
            {{
              selectedCustomer.fullName ||
              selectedCustomer.name ||
              selectedCustomer.society ||
              "Particulier"
            }}
          </span>
          <span v-else class="placeholder">
            <i class="bi bi-person-plus"></i>
            Sélectionner un client
          </span>
          <i class="bi bi-chevron-down"></i>
        </button>
      </div>

      <!-- Payment Summary -->
      <div class="payment-summary">
        <h4>Résumé du paiement</h4>
        <div class="summary-row total-row">
          <span>Total à payer</span>
          <span>{{ formatPrice(totalPayment) }} MAD</span>
        </div>
      </div>

      <!-- Place Order Button -->
      <button
        class="place-order-btn"
        @click="placeOrder"
        :disabled="!canPlaceOrder"
      >
        <i class="bi bi-check-circle"></i>
        {{ placingOrder ? "Enregistrement..." : "Enregistrer la commande" }}
      </button>
    </div>

    <!-- Customer Modal -->
    <div
      v-if="showCustomerModal"
      class="customer-modal-overlay"
      @click="showCustomerModal = false"
    >
      <div class="customer-modal" @click.stop>
        <header class="customer-modal-header">
          <div>
            <h3>Sélectionner un client</h3>
            <p>Choisissez le client pour cette vente</p>
          </div>
          <button
            class="btn btn-light icon-only"
            @click="showCustomerModal = false"
          >
            <i class="bi bi-x-lg"></i>
          </button>
        </header>

        <section class="customer-modal-body">
          <div class="customer-search">
            <i class="bi bi-search"></i>
            <input
              v-model="customerSearchTerm"
              type="search"
              placeholder="Rechercher un client (nom, téléphone, ville...)"
            />
          </div>

          <div v-if="filteredCustomers.length === 0" class="customer-state">
            <i class="bi bi-people"></i>
            <p>Aucun client trouvé</p>
          </div>

          <div v-else class="customer-list">
            <button
              v-for="customer in filteredCustomers"
              :key="customer.reference || customer.id"
              type="button"
              class="customer-item"
              @click="selectCustomer(customer)"
            >
              <div class="customer-item-content">
                <div class="customer-avatar">
                  {{ getCustomerInitials(customer) }}
                </div>
                <div class="customer-details">
                  <h4>
                    {{
                      customer.fullName ||
                      customer.name ||
                      customer.society ||
                      customer.reference
                    }}
                  </h4>
                  <div class="customer-meta">
                    <span v-if="customer.society" class="meta-item">
                      <i class="bi bi-building"></i>
                      {{ customer.society }}
                    </span>
                    <span v-if="customer.phone" class="meta-item">
                      <i class="bi bi-telephone"></i>
                      {{ customer.phone }}
                    </span>
                    <span
                      v-if="customer.city || customer.address"
                      class="meta-item"
                    >
                      <i class="bi bi-geo-alt"></i>
                      {{ customer.city || customer.address }}
                    </span>
                  </div>
                </div>
              </div>
              <i class="bi bi-chevron-right"></i>
            </button>
          </div>
        </section>
      </div>
    </div>

    <!-- Barcode Scanner Modal -->
    <BarcodeScannerModal
      :show="showScanner"
      @close="closeScanner"
      @scanned="onBarcodeScanned"
      :api-endpoint="'/api/items/read-barcode'"
    />

    <!-- Payment Modal -->
    <div
      v-if="showPaymentModal"
      class="modal-overlay"
      @click.self="closePaymentModal"
    >
      <div class="payment-modal">
        <div class="payment-modal-header">
          <h3>
            <i class="bi bi-cash-coin"></i>
            Ajouter un paiement
          </h3>
          <button class="modal-close-btn" @click="closePaymentModal">
            <i class="bi bi-x"></i>
          </button>
        </div>

        <div class="payment-modal-body">
          <div class="payment-info">
            <div class="info-row">
              <span class="info-label">Montant total:</span>
              <span class="info-value total-amount">
                {{ formatPrice(totalPayment) }} MAD
              </span>
            </div>
          </div>

          <div class="payment-form">
            <div class="form-group">
              <label>Montant reçu <span class="required">*</span></label>
              <div class="amount-buttons">
                <button
                  v-for="amount in [200, 100, 50, 20, 10]"
                  :key="amount"
                  type="button"
                  class="amount-btn"
                  :class="{
                    active: paymentForm.selectedAmounts.includes(amount),
                  }"
                  @click="toggleAmount(amount)"
                >
                  {{ amount }} DH
                </button>
              </div>
              <input
                v-model.number="paymentForm.amountReceived"
                type="number"
                step="0.01"
                class="form-input"
                placeholder="0.00"
                @input="calculateChange"
              />
            </div>

            <div v-if="paymentForm.change > 0" class="change-display">
              <div class="change-label">Retour au client:</div>
              <div class="change-amount">
                {{ formatPrice(paymentForm.change) }} MAD
              </div>
            </div>

            <div class="form-group">
              <label>Date <span class="required">*</span></label>
              <input
                v-model="paymentForm.date"
                type="date"
                class="form-input"
              />
            </div>
          </div>
        </div>

        <div class="payment-modal-footer">
          <button class="btn-cancel" @click="skipPayment">Passer</button>
          <button
            class="btn-confirm"
            @click="addPayment"
            :disabled="
              !paymentForm.amountReceived ||
              paymentForm.amountReceived <= 0 ||
              paymentForm.amountReceived < totalPayment ||
              addingPayment
            "
          >
            <i v-if="!addingPayment" class="bi bi-check-circle"></i>
            <span v-else class="spinner-small"></span>
            {{
              addingPayment ? "Enregistrement..." : "Enregistrer le paiement"
            }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters, mapActions } from "vuex";
import BarcodeScannerModal from "@/components/BarcodeScannerModal.vue";

export default {
  name: "QuickSale",
  components: {
    BarcodeScannerModal,
  },
  data() {
    return {
      searchTerm: "",
      searchMode: "barcode", // 'barcode' or 'code'
      barcodeSearch: "",
      itemSearchTerm: "",
      searchingItem: false,
      searchingItems: false,
      itemSearchResults: [],
      searchTimeout: null,
      selectedCategory: "all",
      showFilter: false,
      showScanner: false,
      showCustomerModal: false,
      showPaymentModal: false,
      customerSearchTerm: "",
      paymentMethod: "espece", // Default payment method (always "espece")
      currentOrderId: this.generateOrderId(),
      placingOrder: false,
      addingPayment: false,
      createdSaleReference: null,
      paymentForm: {
        amount: 0,
        amountReceived: 0,
        change: 0,
        selectedAmounts: [],
        date: new Date().toISOString().split("T")[0],
      },
      menuItems: [],
      categories: [],
      focusInterval: null,
      selectedCustomer: null,
    };
  },
  computed: {
    ...mapGetters("cart", {
      cartItems: "getItems",
    }),
    ...mapGetters("customer", {
      customers: "getAll",
    }),
    filteredMenuItems() {
      let items = this.menuItems || [];

      // Only show items if there's an active filter (category or search)
      const hasCategoryFilter = this.selectedCategory !== "all";
      const hasSearchFilter =
        this.searchTerm && this.searchTerm.trim().length > 0;

      // If no filter is active, return empty array
      if (!hasCategoryFilter && !hasSearchFilter) {
        return [];
      }

      // Filter by category
      if (hasCategoryFilter) {
        items = items.filter(
          (item) => item.section_reference === this.selectedCategory
        );
      }

      // Filter by search term
      if (hasSearchFilter) {
        const term = this.searchTerm.toLowerCase().trim();
        items = items.filter(
          (item) =>
            (item.fullName || item.name || "").toLowerCase().includes(term) ||
            (item.code || "").toLowerCase().includes(term) ||
            (item.reference || "").toLowerCase().includes(term)
        );
      }

      return items;
    },
    visibleItemSearchResults() {
      if (this.searchMode !== "code") {
        return [];
      }
      return this.itemSearchResults;
    },
    filteredCustomers() {
      if (!this.customerSearchTerm) return this.customers || [];
      const term = this.customerSearchTerm.toLowerCase();
      return (this.customers || []).filter((customer) =>
        [
          customer.fullName,
          customer.name,
          customer.society,
          customer.phone,
          customer.email,
          customer.city,
          customer.address,
          customer.reference,
        ]
          .filter(Boolean)
          .map((value) => value.toString().toLowerCase())
          .some((value) => value.includes(term))
      );
    },
    subTotal() {
      return this.cartItems.reduce((sum, item) => {
        const total = item.totalPriceTTC || item.total || 0;
        return sum + parseFloat(total || 0);
      }, 0);
    },
    totalPayment() {
      return this.subTotal;
    },
    canPlaceOrder() {
      return (
        this.cartItems.length > 0 &&
        !this.placingOrder &&
        !!(this.selectedCustomer && this.selectedCustomer.reference)
      );
    },
  },
  mounted() {
    this.loadMenuItems();
    this.loadCategories();
    this.loadCartItems();
    this.loadCustomers();
    // Focus on barcode input after component is mounted
    this.$nextTick(() => {
      if (this.$refs.barcodeInput) {
        this.$refs.barcodeInput.focus();
      }
    });
    // Start interval to keep barcode input focused all the time
    this.startBarcodeInputFocusInterval();
  },
  beforeUnmount() {
    // Clear interval when component is destroyed
    if (this.focusInterval) {
      clearInterval(this.focusInterval);
      this.focusInterval = null;
    }
    // Clear search timeout
    if (this.searchTimeout) {
      clearTimeout(this.searchTimeout);
      this.searchTimeout = null;
    }
  },
  watch: {
    // Keep input focused when cart items change
    cartItems() {
      this.$nextTick(() => {
        if (this.searchMode === "barcode" && this.$refs.barcodeInput) {
          this.$refs.barcodeInput.focus();
        } else if (this.searchMode === "code" && this.$refs.itemSearchInput) {
          this.$refs.itemSearchInput.focus();
        }
      });
    },
    // Focus appropriate input when search mode changes
    searchMode() {
      this.$nextTick(() => {
        if (this.searchMode === "barcode" && this.$refs.barcodeInput) {
          this.$refs.barcodeInput.focus();
        } else if (this.searchMode === "code" && this.$refs.itemSearchInput) {
          this.$refs.itemSearchInput.focus();
        }
      });
    },
  },
  methods: {
    ...mapActions("cart", ["getItems", "destroyItem"]),
    ...mapActions("category", ["getAll"]),
    ...mapActions("customer", ["getAll"]),
    setSearchMode(mode) {
      this.searchMode = mode;
      if (mode === "code" && !this.itemSearchTerm.trim()) {
        this.showDefaultItemSearchResults();
      }
      this.$nextTick(() => {
        if (mode === "barcode" && this.$refs.barcodeInput) {
          this.$refs.barcodeInput.focus();
        } else if (mode === "code" && this.$refs.itemSearchInput) {
          this.$refs.itemSearchInput.focus();
        }
      });
    },
    startBarcodeInputFocusInterval() {
      // Focus input every 0.001 seconds (1 millisecond) to keep it always focused
      this.focusInterval = setInterval(() => {
        if (
          !this.showPaymentModal &&
          !this.showCustomerModal &&
          !this.showScanner
        ) {
          // Only focus if modals are not open
          if (this.searchMode === "barcode" && this.$refs.barcodeInput) {
            if (document.activeElement !== this.$refs.barcodeInput) {
              this.$refs.barcodeInput.focus();
            }
          } else if (this.searchMode === "code" && this.$refs.itemSearchInput) {
            if (document.activeElement !== this.$refs.itemSearchInput) {
              this.$refs.itemSearchInput.focus();
            }
          }
        }
      }, 1); // 1 millisecond = 0.001 seconds
    },
    keepBarcodeInputFocused() {
      if (
        this.$refs.barcodeInput &&
        !this.showPaymentModal &&
        !this.showCustomerModal &&
        !this.showScanner
      ) {
        if (document.activeElement !== this.$refs.barcodeInput) {
          this.$refs.barcodeInput.focus();
        }
      }
    },
    generateOrderId() {
      return Math.floor(100000 + Math.random() * 900000).toString();
    },
    async loadMenuItems() {
      try {
        const response = await this.$api.fetch("/api/items");
        this.menuItems = Array.isArray(response)
          ? response
          : response?.data || [];
        if (this.searchMode === "code" && !this.itemSearchTerm.trim()) {
          this.showDefaultItemSearchResults();
        }
      } catch (error) {
        console.error("Error loading menu items:", error);
        this.$toast?.error?.("Impossible de charger les articles");
      }
    },
    async loadCategories() {
      try {
        await this.$store.dispatch("category/getAll");
        const categories = this.$store.getters["category/getAll"];
        this.categories = Array.isArray(categories)
          ? categories
          : categories?.data || [];
      } catch (error) {
        console.error("Error loading categories:", error);
      }
    },
    async loadCartItems() {
      try {
        await this.getItems();
      } catch (error) {
        console.error("Error loading cart items:", error);
      }
    },
    async loadCustomers() {
      try {
        await this.$store.dispatch("customer/getAll");
      } catch (error) {
        console.error("Error loading customers:", error);
      }
    },
    selectCategory(categoryRef) {
      this.selectedCategory = categoryRef;
    },
    filterItems() {
      // Filtering is handled in computed property
    },
    async searchItem() {
      if (!this.searchTerm) return;
      // Search functionality can be enhanced here
    },
    onBarcodeInput() {
      // Keep input focused
      this.$nextTick(() => {
        if (this.$refs.barcodeInput) {
          this.$refs.barcodeInput.focus();
        }
      });
    },
    showDefaultItemSearchResults() {
      this.itemSearchResults = (this.menuItems || []).slice(0, 80);
    },
    getItemSearchValues(item) {
      return [
        item?.reference,
        item?.code,
        item?.barCode,
        item?.barcode,
        item?.fullName,
        item?.name,
        item?.designation,
        item?.section?.fullName,
        item?.section_reference,
      ]
        .filter(Boolean)
        .map((value) => value.toString().toLowerCase());
    },
    itemMatchesSearch(item, searchTerm) {
      return this.getItemSearchValues(item).some((value) =>
        value.includes(searchTerm)
      );
    },
    onItemSearchInput() {
      // Clear results when search term is empty
      if (!this.itemSearchTerm || this.itemSearchTerm.trim().length === 0) {
        this.showDefaultItemSearchResults();
        return;
      }
      // Debounce search - search after user stops typing
      if (this.searchTimeout) {
        clearTimeout(this.searchTimeout);
      }
      this.searchTimeout = setTimeout(() => {
        this.searchItems();
      }, 300); // Wait 300ms after user stops typing
    },
    async searchItems() {
      if (!this.itemSearchTerm || !this.itemSearchTerm.trim()) {
        this.showDefaultItemSearchResults();
        return;
      }

      this.searchingItems = true;
      try {
        if (!this.menuItems.length) {
          await this.loadMenuItems();
        }

        const searchTerm = this.itemSearchTerm.toLowerCase().trim();
        this.itemSearchResults = (this.menuItems || [])
          .filter((item) => this.itemMatchesSearch(item, searchTerm))
          .slice(0, 80);
      } catch (error) {
        console.error("Error searching items:", error);
        this.$toast?.error?.("Erreur lors de la recherche d'articles");
      } finally {
        this.searchingItems = false;
      }
    },
    async addFirstSearchResult() {
      const item = this.visibleItemSearchResults[0];
      if (item) {
        await this.addItemFromSearch(item);
      }
    },
    async addItemFromSearch(item) {
      // Check if item already exists in cart
      const existingItem = this.cartItems.find(
        (ci) =>
          ci.item_reference === item.reference ||
          (ci.item && ci.item.reference === item.reference)
      );

      if (existingItem) {
        const added = await this.addItemToCart(item, { showToast: false });
        if (!added) return;
        this.$toast?.success?.(
          "Quantité augmentée pour " + (item.fullName || item.name)
        );
      } else {
        // Add new item to cart with quantity 1
        const added = await this.addItemToCart(item, { showToast: false });
        if (!added) return;
        this.$toast?.success?.(
          "Article ajouté: " + (item.fullName || item.name)
        );
      }

      // Clear search results and term
      this.itemSearchTerm = "";
      this.showDefaultItemSearchResults();
      this.$nextTick(() => {
        if (this.$refs.itemSearchInput) {
          this.$refs.itemSearchInput.focus();
        }
      });
    },
    async searchAndAddItem() {
      if (!this.barcodeSearch || !this.barcodeSearch.trim()) {
        return;
      }

      this.searchingItem = true;
      try {
        // Search for item by barcode only
        const response = await this.$api.instance.get(
          "/api/items/find/search",
          {
            params: {
              search: this.barcodeSearch.trim(),
              barcode_only: true,
            },
          }
        );

        if (
          response.data &&
          response.data.status === "success" &&
          response.data.data
        ) {
          const item = response.data.data;

          // Check if item already exists in cart
          const existingItem = this.cartItems.find(
            (ci) =>
              ci.item_reference === item.reference ||
              (ci.item && ci.item.reference === item.reference)
          );

          if (existingItem) {
            const added = await this.addItemToCart(item, { showToast: false });
            if (!added) return;
            this.$toast?.success?.(
              "Quantité augmentée pour " + (item.fullName || item.name)
            );
          } else {
            // Add new item to cart with quantity 1
            await this.addItemToCart(item);
            this.$toast?.success?.(
              "Article ajouté: " + (item.fullName || item.name)
            );
          }

          // Clear search and refocus
          this.barcodeSearch = "";
          this.$nextTick(() => {
            if (this.$refs.barcodeInput) {
              this.$refs.barcodeInput.focus();
            }
          });
        } else {
          this.$toast?.error?.("Article non trouvé");
          // Keep focus on input
          this.$nextTick(() => {
            if (this.$refs.barcodeInput) {
              this.$refs.barcodeInput.focus();
            }
          });
        }
      } catch (error) {
        console.error("Error searching item:", error);
        const message =
          error.response?.data?.message ||
          "Erreur lors de la recherche de l'article";
        this.$toast?.error?.(message);
        // Keep focus on input
        this.$nextTick(() => {
          if (this.$refs.barcodeInput) {
            this.$refs.barcodeInput.focus();
          }
        });
      } finally {
        this.searchingItem = false;
        this.barcodeSearch = "";
        this.$nextTick(() => {
          if (this.$refs.barcodeInput) {
            this.$refs.barcodeInput.focus();
          }
        });
      }
    },
    getItemImage(item) {
      if (!item) return null;
      return (
        item.image ||
        item.item?.image ||
        item.item_image ||
        item.image_url ||
        null
      );
    },
    handleImageError(event) {
      if (event && event.target) {
        event.target.style.display = "none";
        const placeholder = event.target.nextElementSibling;
        if (placeholder) {
          placeholder.style.display = "flex";
        }
      }
    },
    getItemPrice(item) {
      if (!item) return 0;
      return (
        parseFloat(item.priceTTC) ||
        parseFloat(item.price_ttc) ||
        parseFloat(item.price) ||
        parseFloat(item.item?.priceTTC) ||
        0
      );
    },
    getItemStock(item) {
      if (!item) return null;
      return (
        item.quantity ||
        item.stock ||
        item.stockQuantity ||
        item.item?.quantity ||
        null
      );
    },
    formatStock(stock) {
      if (stock === null || stock === undefined) return "0";
      return parseFloat(stock).toFixed(0);
    },
    formatPrice(price) {
      const num = parseFloat(price) || 0;
      return num.toLocaleString("fr-FR", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      });
    },
    getItemQuantity(item) {
      if (!item) return 0;
      const ref = item.reference || item.id;
      const cartItem = this.cartItems.find(
        (ci) =>
          ci.item_reference === ref ||
          ci.item?.reference === ref ||
          (ci.item && ci.item.reference === ref)
      );
      return cartItem ? parseFloat(cartItem.quantity || 0) : 0;
    },
    async increaseQuantity(item) {
      const ref = item.reference || item.id;
      const existingItem = this.cartItems.find(
        (ci) =>
          ci.item_reference === ref ||
          ci.item?.reference === ref ||
          (ci.item && ci.item.reference === ref)
      );

      if (existingItem) {
        await this.addItemToCart(existingItem, { showToast: false });
      } else {
        // Add new item to cart
        await this.addItemToCart(item);
      }
    },
    async decreaseQuantity(item) {
      const ref = item.reference || item.id;
      const existingItem = this.cartItems.find(
        (ci) =>
          ci.item_reference === ref ||
          ci.item?.reference === ref ||
          (ci.item && ci.item.reference === ref)
      );

      if (existingItem) {
        const currentQty = parseFloat(existingItem.quantity || 0);
        if (currentQty > 1) {
          await this.updateCartItemQuantity(existingItem, currentQty - 1);
        } else {
          await this.removeItemFromCart(existingItem);
        }
      }
    },
    async addItemToCart(item, options = {}) {
      try {
        const showToast = options.showToast !== false;
        const price = this.getItemPrice(item);
        const payload = {
          item_reference:
            item.reference || item.item_reference || item.item?.reference || item.id,
          quantity: 1,
          priceTTC: price,
          totalPriceTTC: price * 1,
        };
        // Call API to add item to sale_items
        await this.$store.dispatch("sale/storeItem", payload);
        await this.loadCartItems();
        if (!showToast) {
          return true;
        }
        this.$toast?.success?.("Article ajouté au panier");
      } catch (error) {
        console.error("Error adding item to cart:", error);
        this.$toast?.error?.("Impossible d'ajouter l'article au panier");
      }
    },
    async increaseCartItemQuantity(cartItem) {
      const added = await this.addItemToCart(cartItem, { showToast: false });
      if (added) {
        this.$toast?.success?.("QuantitÃ© mise Ã  jour");
      }
    },
    async decreaseCartItemQuantity(cartItem) {
      const currentQty = parseFloat(cartItem.quantity || 0);
      if (currentQty > 1) {
        await this.updateCartItemQuantity(cartItem, currentQty - 1);
      } else {
        await this.removeItemFromCart(cartItem);
      }
    },
    async updateCartItemQuantity(cartItem, newQuantity) {
      try {
        const price = parseFloat(cartItem.priceTTC || cartItem.price || 0);

        // Get sale_item id and item_reference
        const saleItemId =
          cartItem.id || cartItem.reference || cartItem.sale_item_id;
        const itemReference =
          cartItem.item_reference ||
          cartItem.item?.reference ||
          cartItem.item_reference;

        const payload = {
          id: saleItemId,
          item_reference: itemReference,
          priceTTC: price,
          quantity: newQuantity,
          totalPriceTTC: price * newQuantity,
        };

        // Update via API
        const ref = saleItemId;
        await this.$api.update(`/api/sales_items/${ref}`, payload, {
          confirm: false,
        });
        await this.loadCartItems();
        this.$toast?.success?.("Quantité mise à jour");
      } catch (error) {
        console.error("Error updating cart item:", error);
        this.$toast?.error?.("Impossible de mettre à jour la quantité");
      }
    },
    async removeItemFromCart(cartItem) {
      try {
        const ref =
          cartItem.id || cartItem.reference || cartItem.item_reference;
        await this.destroyItem(ref);
        await this.loadCartItems();
        this.$toast?.success?.("Article retiré du panier");
      } catch (error) {
        console.error("Error removing item from cart:", error);
        this.$toast?.error?.("Impossible de retirer l'article");
      }
    },
    getOrderItemImage(orderItem) {
      if (!orderItem) return null;
      // Try different possible structures
      if (orderItem.item) {
        return (
          orderItem.item.image ||
          orderItem.item.image_url ||
          orderItem.item.item_image ||
          null
        );
      }
      return orderItem.image || orderItem.image_url || null;
    },
    getOrderItemName(orderItem) {
      if (!orderItem) return "Article";
      if (orderItem.item) {
        return (
          orderItem.item.fullName ||
          orderItem.item.name ||
          orderItem.item.designation ||
          "Article"
        );
      }
      return (
        orderItem.itemFullName ||
        orderItem.item_full_name ||
        orderItem.designation ||
        orderItem.name ||
        "Article"
      );
    },
    selectCustomer(customer) {
      if (!customer || !customer.reference) return;
      this.selectedCustomer = customer;
      this.showCustomerModal = false;
      this.customerSearchTerm = "";
    },
    getCustomerInitials(customer) {
      const name = customer.fullName || customer.name || customer.society || "";
      if (!name) return "?";
      const parts = name.trim().split(/\s+/);
      const initials = [parts[0], parts[parts.length - 1]]
        .filter(Boolean)
        .map((part) => part[0]?.toUpperCase())
        .join("");
      return initials || name.charAt(0).toUpperCase();
    },
    editOrderItem(item) {
      // Open edit modal or navigate to edit page
      this.$toast?.info?.("Fonctionnalité de modification à venir");
    },
    async placeOrder() {
      if (this.cartItems.length === 0) {
        this.$toast?.error?.("Le panier est vide");
        return;
      }

      if (!this.selectedCustomer || !this.selectedCustomer.reference) {
        this.$toast?.error?.("Veuillez sélectionner un client");
        return;
      }

      this.placingOrder = true;
      try {
        // Create sale via API
        const saleData = {
          reference: `SALE-${this.currentOrderId}`,
          customer_reference: this.selectedCustomer.reference,
          date: new Date().toISOString().split("T")[0],
          total_amount: this.totalPayment,
          remark: `Paiement: ${this.paymentMethod}`,
        };

        // Call API to create sale using sales endpoint
        const saleResponse = await this.$store.dispatch("sale/store", saleData);

        // Get the sale reference from response
        const saleReference =
          saleResponse?.data?.reference ||
          saleResponse?.reference ||
          saleData.reference;

        this.createdSaleReference = saleReference;
        this.$toast?.success?.("Vente enregistrée avec succès!");

        // Set default payment amount to total
        this.paymentForm.amount = this.totalPayment;
        this.paymentForm.amountReceived = this.totalPayment;
        this.paymentForm.change = 0;
        this.paymentForm.selectedAmounts = [];
        this.paymentForm.date = new Date().toISOString().split("T")[0];

        // Show payment modal
        this.showPaymentModal = true;
      } catch (error) {
        console.error("Error placing order:", error);
        this.$toast?.error?.("Impossible d'enregistrer la commande");
      } finally {
        this.placingOrder = false;
      }
    },
    openScanner() {
      this.showScanner = true;
    },
    closeScanner() {
      this.showScanner = false;
    },
    async onBarcodeScanned(barcode) {
      this.closeScanner();
      this.barcodeSearch = barcode;
      // Trigger search and add
      await this.searchAndAddItem();
    },
    closePaymentModal() {
      this.showPaymentModal = false;
      // Clear cart and reset after closing modal
      this.currentOrderId = this.generateOrderId();
      this.createdSaleReference = null;
      this.paymentForm = {
        amount: 0,
        amountReceived: 0,
        change: 0,
        selectedAmounts: [],
        date: new Date().toISOString().split("T")[0],
      };
      this.loadCartItems();
      // Navigate to sales list
      this.$router.push({ name: "sales-list" });
    },
    async skipPayment() {
      this.closePaymentModal();
    },
    toggleAmount(amount) {
      const index = this.paymentForm.selectedAmounts.indexOf(amount);
      if (index > -1) {
        // Remove if already selected
        this.paymentForm.selectedAmounts.splice(index, 1);
      } else {
        // Add if not selected
        this.paymentForm.selectedAmounts.push(amount);
      }
      // Calculate total from selected amounts
      const totalSelected = this.paymentForm.selectedAmounts.reduce(
        (sum, amt) => sum + amt,
        0
      );
      this.paymentForm.amountReceived = totalSelected;
      this.calculateChange();
    },
    calculateChange() {
      const received = parseFloat(this.paymentForm.amountReceived || 0);
      const total = parseFloat(this.totalPayment || 0);
      this.paymentForm.change = Math.max(0, received - total);
      // Set payment amount to total (not the received amount)
      this.paymentForm.amount = total;
    },
    async addPayment() {
      if (
        !this.paymentForm.amountReceived ||
        this.paymentForm.amountReceived <= 0
      ) {
        this.$toast?.error?.("Veuillez saisir le montant reçu");
        return;
      }

      if (this.paymentForm.amountReceived < this.totalPayment) {
        this.$toast?.error?.(
          "Le montant reçu doit être supérieur ou égal au montant total"
        );
        return;
      }

      this.addingPayment = true;
      try {
        const paymentData = {
          sale_reference: this.createdSaleReference,
          customer_reference: this.selectedCustomer.reference,
          amount: this.paymentForm.amount, // Amount to pay (total)
          date: this.paymentForm.date,
          paymentMethod: this.paymentMethod,
          remark: `Paiement ${this.paymentMethod} pour la vente ${
            this.createdSaleReference
          }${
            this.paymentForm.change > 0
              ? ` - Retour: ${this.paymentForm.change.toFixed(2)} MAD`
              : ""
          }`,
        };

        // Use payments API for sale payments
        await this.$api.create("/api/payments", paymentData, {
          confirm: false,
        });
        this.$toast?.success?.(
          this.paymentForm.change > 0
            ? `Paiement enregistré! Retour: ${this.formatPrice(
                this.paymentForm.change
              )} MAD`
            : "Paiement enregistré avec succès!"
        );
        this.closePaymentModal();
      } catch (paymentError) {
        console.error("Error creating payment:", paymentError);
        this.$toast?.error?.("Impossible d'enregistrer le paiement");
      } finally {
        this.addingPayment = false;
      }
    },
  },
};
</script>

<style scoped>
.pos-container {
  display: flex;
  height: calc(100vh - 60px);
  background: #ffffff;
  overflow: hidden;
  position: relative;
}

/* Fixed Cart Sidebar (Left) */
.cart-sidebar-fixed {
  width: 380px;
  background: #ffffff;
  border-right: 1px solid #e2e8f0;
  display: flex;
  flex-direction: column;
  height: 100%;
  position: fixed;
  left: 0;
  top: 60px;
  z-index: 10;
  box-shadow: 2px 0 10px rgba(0, 0, 0, 0.05);
}

.cart-sidebar-header {
  padding: 1.5rem;
  border-bottom: 1px solid #e2e8f0;
  background: #f8f9fa;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.cart-sidebar-header h3 {
  margin: 0;
  font-size: 1.25rem;
  font-weight: 700;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.cart-badge {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: #ffffff;
  padding: 0.25rem 0.75rem;
  border-radius: 12px;
  font-size: 0.85rem;
  font-weight: 700;
  min-width: 28px;
  text-align: center;
}

.cart-sidebar-content {
  flex: 1;
  overflow-y: auto;
  padding: 0.5rem;
  display: flex;
  flex-direction: column;
  gap: 0.4rem;
}

.cart-sidebar-item {
  display: flex;
  gap: 0.4rem;
  padding: 0.4rem;
  background: #f8f9fa;
  border: 1px solid #e2e8f0;
  border-radius: 6px;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

.cart-sidebar-item:hover {
  background: #ffffff;
  border-color: #667eea;
  box-shadow: 0 4px 15px rgba(102, 126, 234, 0.15);
  transform: translateX(4px);
}

.cart-sidebar-item-image {
  width: 35px;
  height: 35px;
  border-radius: 6px;
  overflow: hidden;
  background: #e2e8f0;
  flex-shrink: 0;
}

.cart-sidebar-item-image img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.item-thumb-placeholder {
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #cbd5e0;
  font-size: 1.5rem;
}

.cart-sidebar-item-details {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 0.15rem;
}

.cart-sidebar-item-name {
  margin: 0;
  font-size: 0.75rem;
  font-weight: 600;
  color: #1a202c;
  line-height: 1.1;
  overflow: hidden;
  text-overflow: ellipsis;
  display: -webkit-box;
  -webkit-line-clamp: 1;
  -webkit-box-orient: vertical;
}

.cart-sidebar-item-meta {
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-size: 0.7rem;
}

.cart-sidebar-qty {
  color: #64748b;
  font-weight: 500;
}

.cart-sidebar-price {
  color: #667eea;
  font-weight: 700;
}

.cart-sidebar-item-actions {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 0.2rem;
}

.cart-sidebar-quantity-controls {
  display: flex;
  align-items: center;
  gap: 0.2rem;
  background: rgba(102, 126, 234, 0.05);
  padding: 0.1rem;
  border-radius: 4px;
}

.qty-btn-sm {
  width: 18px;
  height: 18px;
  border-radius: 3px;
  border: 1px solid rgba(102, 126, 234, 0.3);
  background: #ffffff;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s;
  color: #667eea;
  font-size: 0.65rem;
  padding: 0;
}

.qty-btn-sm:hover {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  border-color: transparent;
  color: #ffffff;
}

.qty-value-sm {
  font-size: 0.7rem;
  font-weight: 600;
  color: #1a202c;
  min-width: 16px;
  text-align: center;
}

.remove-btn-sm {
  width: 18px;
  height: 18px;
  border-radius: 3px;
  border: 1px solid rgba(239, 68, 68, 0.3);
  background: rgba(239, 68, 68, 0.1);
  color: #ef4444;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s;
  font-size: 0.65rem;
  padding: 0;
}

.remove-btn-sm:hover {
  background: #ef4444;
  color: #ffffff;
  border-color: #ef4444;
}

.empty-cart-sidebar {
  text-align: center;
  padding: 3rem 1rem;
  color: #a0aec0;
}

.empty-cart-sidebar i {
  font-size: 3rem;
  margin-bottom: 1rem;
  display: block;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  opacity: 0.5;
}

.cart-sidebar-footer {
  padding: 1.5rem;
  border-top: 1px solid #e2e8f0;
  background: #f8f9fa;
}

.cart-sidebar-total {
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-size: 1.1rem;
  font-weight: 600;
}

.cart-sidebar-total span {
  color: #4a5568;
}

.cart-sidebar-total strong {
  font-size: 1.3rem;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
}

/* Main Content Area */
.pos-main-content {
  flex: 1;
  display: flex;
  flex-direction: column;
  padding: 2rem;
  overflow-y: auto;
  background: #ffffff;
  position: relative;
  margin-left: 380px;
}

.search-filter-section {
  display: flex;
  flex-direction: column;
  gap: 1rem;
  margin-bottom: 1.5rem;
}

.search-mode-selector {
  display: flex;
  gap: 0.5rem;
  margin-bottom: 0.5rem;
}

.search-mode-btn {
  flex: 1;
  padding: 0.75rem 1rem;
  border: 2px solid #e2e8f0;
  border-radius: 12px;
  background: #ffffff;
  color: #64748b;
  font-size: 0.95rem;
  font-weight: 600;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

.search-mode-btn:hover {
  border-color: #667eea;
  background: #f0f4ff;
  color: #667eea;
}

.search-mode-btn.active {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  border-color: transparent;
  color: #ffffff;
  box-shadow: 0 4px 15px rgba(102, 126, 234, 0.3);
}

.search-mode-btn i {
  font-size: 1.1rem;
}

.search-form {
  flex: 1;
  display: flex;
  gap: 0.75rem;
  align-items: center;
}

.search-box {
  flex: 1;
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 1rem 1.25rem;
  background: #ffffff;
  border: 2px solid #667eea;
  border-radius: 16px;
  box-shadow: 0 4px 20px rgba(102, 126, 234, 0.2);
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

.search-box:focus-within {
  border-color: #667eea;
  box-shadow: 0 6px 30px rgba(102, 126, 234, 0.3);
  transform: translateY(-2px);
}

.search-box i {
  color: #667eea;
  font-size: 1.2rem;
  transition: transform 0.3s;
}

.search-box:focus-within i {
  transform: scale(1.1);
}

.search-box input {
  flex: 1;
  border: none;
  outline: none;
  font-size: 1.1rem;
  color: #1a202c;
  background: transparent;
  font-weight: 600;
}

.search-box .spinner-small {
  width: 16px;
  height: 16px;
  border: 2px solid rgba(102, 126, 234, 0.3);
  border-top-color: #667eea;
  border-radius: 50%;
  animation: spin 0.6s linear infinite;
  flex-shrink: 0;
}

.search-box input::placeholder {
  color: #a0aec0;
  font-weight: 400;
}

.search-submit-btn {
  padding: 1rem 1.75rem;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: #ffffff;
  border: none;
  border-radius: 16px;
  font-size: 1rem;
  font-weight: 700;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  box-shadow: 0 4px 15px rgba(102, 126, 234, 0.4);
  white-space: nowrap;
}

.search-submit-btn:hover:not(:disabled) {
  transform: translateY(-2px);
  box-shadow: 0 6px 25px rgba(102, 126, 234, 0.5);
}

.search-submit-btn:active:not(:disabled) {
  transform: translateY(0);
}

.search-submit-btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
  transform: none;
}

.spinner-small {
  width: 16px;
  height: 16px;
  border: 2px solid rgba(255, 255, 255, 0.3);
  border-top-color: #ffffff;
  border-radius: 50%;
  animation: spin 0.6s linear infinite;
}

/* Search Results Section */
.search-results-section {
  margin-top: 2rem;
  margin-bottom: 2rem;
}

.search-results-title {
  font-size: 1.25rem;
  font-weight: 700;
  color: #1a202c;
  margin-bottom: 1.5rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.search-results-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
  gap: 1rem;
}

.search-result-card {
  background: #ffffff;
  border: 2px solid #e2e8f0;
  border-radius: 16px;
  padding: 1rem;
  display: flex;
  gap: 1rem;
  cursor: pointer;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
}

.search-result-card:hover {
  transform: translateY(-4px);
  box-shadow: 0 8px 24px rgba(102, 126, 234, 0.2);
  border-color: #667eea;
}

.result-image-wrapper {
  width: 80px;
  height: 80px;
  border-radius: 12px;
  overflow: hidden;
  flex-shrink: 0;
  background: #f7fafc;
  display: flex;
  align-items: center;
  justify-content: center;
}

.result-image-wrapper img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.result-info {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  min-width: 0;
}

.result-name {
  font-size: 1rem;
  font-weight: 700;
  color: #1a202c;
  margin: 0;
  line-height: 1.3;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.result-code {
  font-size: 0.85rem;
  color: #718096;
  margin: 0;
}

.result-stock {
  font-size: 0.85rem;
  color: #48bb78;
  margin: 0;
  font-weight: 600;
}

.result-price {
  font-size: 1.1rem;
  font-weight: 700;
  color: #667eea;
  margin-top: auto;
}

.result-action {
  display: flex;
  align-items: center;
  flex-shrink: 0;
}

.add-item-btn {
  padding: 0.5rem 1rem;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: #ffffff;
  border: none;
  border-radius: 10px;
  font-size: 0.9rem;
  font-weight: 600;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  transition: all 0.2s;
  white-space: nowrap;
}

.add-item-btn:hover {
  transform: scale(1.05);
  box-shadow: 0 4px 12px rgba(102, 126, 234, 0.4);
}

.add-item-btn i {
  font-size: 1.1rem;
}

.empty-search-state {
  text-align: center;
  padding: 4rem 2rem;
  color: #a0aec0;
}

.empty-search-state i {
  font-size: 4rem;
  margin-bottom: 1rem;
  display: block;
  opacity: 0.5;
}

.empty-search-state p {
  font-size: 1.1rem;
  margin: 0;
  color: #718096;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

.filter-btn {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 1rem 1.5rem;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  border: none;
  border-radius: 16px;
  cursor: pointer;
  font-size: 0.95rem;
  font-weight: 600;
  color: #ffffff;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  box-shadow: 0 4px 15px rgba(102, 126, 234, 0.4);
}

.filter-btn:hover {
  transform: translateY(-2px);
  box-shadow: 0 8px 25px rgba(102, 126, 234, 0.5);
}

.filter-btn:active {
  transform: translateY(0);
}

.filter-btn i {
  font-size: 1rem;
}

/* Category Tabs */
.category-tabs {
  display: flex;
  gap: 0.75rem;
  margin-bottom: 2rem;
  flex-wrap: wrap;
  padding: 0.5rem;
  background: #f8f9fa;
  border-radius: 16px;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.03);
}

.category-tab {
  padding: 0.75rem 1.5rem;
  background: rgba(255, 255, 255, 0.8);
  border: 2px solid transparent;
  border-radius: 12px;
  cursor: pointer;
  font-size: 0.9rem;
  color: #4a5568;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  font-weight: 600;
  position: relative;
  overflow: hidden;
}

.category-tab::before {
  content: "";
  position: absolute;
  top: 0;
  left: -100%;
  width: 100%;
  height: 100%;
  background: linear-gradient(
    90deg,
    transparent,
    rgba(255, 255, 255, 0.4),
    transparent
  );
  transition: left 0.5s;
}

.category-tab:hover::before {
  left: 100%;
}

.category-tab:hover {
  background: rgba(255, 255, 255, 1);
  border-color: #667eea;
  transform: translateY(-2px);
  box-shadow: 0 4px 15px rgba(102, 126, 234, 0.2);
}

.category-tab.active {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: #ffffff;
  border-color: transparent;
  box-shadow: 0 4px 20px rgba(102, 126, 234, 0.4);
  transform: translateY(-2px);
}

/* Menu Items Grid */
.menu-items-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(220px, 1fr));
  gap: 1.5rem;
}

.menu-item-card {
  background: #ffffff;
  border-radius: 20px;
  overflow: hidden;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.08);
  transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
  cursor: pointer;
  border: 1px solid #e2e8f0;
  position: relative;
}

.menu-item-card::after {
  content: "";
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: linear-gradient(
    135deg,
    rgba(102, 126, 234, 0.05) 0%,
    rgba(118, 75, 162, 0.05) 100%
  );
  opacity: 0;
  transition: opacity 0.3s;
  pointer-events: none;
}

.menu-item-card:hover {
  transform: translateY(-8px) scale(1.02);
  box-shadow: 0 12px 40px rgba(102, 126, 234, 0.2);
  border-color: #667eea;
}

.menu-item-card:hover::after {
  opacity: 1;
}

.item-image-wrapper {
  width: 100%;
  aspect-ratio: 1;
  overflow: hidden;
  background: linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%);
  position: relative;
}

.item-image-wrapper::before {
  content: "";
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: linear-gradient(
    135deg,
    rgba(102, 126, 234, 0.1) 0%,
    rgba(118, 75, 162, 0.1) 100%
  );
  opacity: 0;
  transition: opacity 0.3s;
  z-index: 1;
}

.menu-item-card:hover .item-image-wrapper::before {
  opacity: 1;
}

.item-image-wrapper img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  transition: transform 0.4s cubic-bezier(0.4, 0, 0.2, 1);
}

.menu-item-card:hover .item-image-wrapper img {
  transform: scale(1.1);
}

.item-image-placeholder {
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #cbd5e0;
  font-size: 3rem;
  background: linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%);
}

.item-info {
  padding: 1rem;
}

.item-name {
  margin: 0 0 0.5rem 0;
  font-size: 1rem;
  font-weight: 600;
  color: #333;
  line-height: 1.3;
}

.item-availability {
  margin: 0 0 0.5rem 0;
  font-size: 0.8rem;
  color: #666;
}

.item-price {
  font-size: 1.25rem;
  font-weight: 700;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  margin-bottom: 0.75rem;
}

.item-quantity-controls {
  display: flex;
  align-items: center;
  gap: 1rem;
  justify-content: center;
  padding: 0.5rem;
  background: rgba(102, 126, 234, 0.05);
  border-radius: 12px;
}

.qty-btn {
  width: 36px;
  height: 36px;
  border-radius: 10px;
  border: 2px solid rgba(102, 126, 234, 0.3);
  background: rgba(255, 255, 255, 0.9);
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  color: #667eea;
  font-weight: 600;
  font-size: 1.1rem;
}

.qty-btn:hover:not(:disabled) {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  border-color: transparent;
  color: #ffffff;
  transform: scale(1.1);
  box-shadow: 0 4px 15px rgba(102, 126, 234, 0.4);
}

.qty-btn:active:not(:disabled) {
  transform: scale(0.95);
}

.qty-btn:disabled {
  opacity: 0.4;
  cursor: not-allowed;
  transform: none;
}

.qty-value {
  font-size: 1rem;
  font-weight: 600;
  color: #333;
  min-width: 24px;
  text-align: center;
}

.empty-menu {
  grid-column: 1 / -1;
  text-align: center;
  padding: 4rem 1rem;
  color: #a0aec0;
}

.empty-menu i {
  font-size: 4rem;
  margin-bottom: 1rem;
  display: block;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  opacity: 0.5;
}

/* Cart Items View */
.cart-items-view {
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
}

.cart-items-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1rem;
  background: #f8f9fa;
  border-radius: 12px;
}

.cart-items-header h3 {
  margin: 0;
  font-size: 1.25rem;
  font-weight: 700;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
}

.cart-count {
  font-size: 0.9rem;
  color: #667eea;
  font-weight: 600;
  padding: 0.5rem 1rem;
  background: rgba(102, 126, 234, 0.1);
  border-radius: 10px;
}

.cart-items-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: 1.5rem;
}

.cart-item-card {
  background: #ffffff;
  border-radius: 20px;
  overflow: hidden;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.08);
  transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
  border: 1px solid #e2e8f0;
  position: relative;
  display: flex;
  flex-direction: column;
}

.cart-item-card:hover {
  transform: translateY(-4px);
  box-shadow: 0 8px 30px rgba(102, 126, 234, 0.2);
  border-color: #667eea;
}

.cart-item-image-wrapper {
  width: 100%;
  aspect-ratio: 16/9;
  overflow: hidden;
  background: linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%);
  position: relative;
}

.cart-item-image-wrapper img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  transition: transform 0.4s cubic-bezier(0.4, 0, 0.2, 1);
}

.cart-item-card:hover .cart-item-image-wrapper img {
  transform: scale(1.05);
}

.cart-item-info {
  padding: 1.25rem;
  display: flex;
  flex-direction: column;
  gap: 1rem;
  flex: 1;
}

.cart-item-name {
  margin: 0;
  font-size: 1.1rem;
  font-weight: 600;
  color: #1a202c;
  line-height: 1.3;
}

.cart-item-details {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

.cart-item-price-row,
.cart-item-quantity-row,
.cart-item-total-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.5rem 0;
}

.price-label,
.qty-label,
.total-label {
  font-size: 0.9rem;
  color: #64748b;
  font-weight: 500;
}

.price-value {
  font-size: 0.95rem;
  color: #4a5568;
  font-weight: 600;
}

.total-value {
  font-size: 1.1rem;
  font-weight: 700;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
}

.cart-item-quantity-controls {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.25rem;
  background: rgba(102, 126, 234, 0.05);
  border-radius: 10px;
}

.remove-item-btn {
  padding: 0.75rem 1rem;
  background: rgba(239, 68, 68, 0.1);
  color: #ef4444;
  border: 1px solid rgba(239, 68, 68, 0.2);
  border-radius: 10px;
  cursor: pointer;
  font-size: 0.9rem;
  font-weight: 600;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  margin-top: auto;
}

.remove-item-btn:hover {
  background: #ef4444;
  color: #ffffff;
  transform: translateY(-2px);
  box-shadow: 0 4px 15px rgba(239, 68, 68, 0.3);
}

.empty-cart-main {
  grid-column: 1 / -1;
  text-align: center;
  padding: 4rem 1rem;
  color: #a0aec0;
}

.empty-cart-main i {
  font-size: 4rem;
  margin-bottom: 1rem;
  display: block;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  opacity: 0.5;
}

.btn-view-menu {
  margin-top: 1.5rem;
  padding: 0.75rem 1.5rem;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: #ffffff;
  border: none;
  border-radius: 12px;
  font-size: 0.95rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  box-shadow: 0 4px 15px rgba(102, 126, 234, 0.3);
}

.btn-view-menu:hover {
  transform: translateY(-2px);
  box-shadow: 0 6px 20px rgba(102, 126, 234, 0.4);
}

/* Right Sidebar */
.pos-sidebar {
  width: 420px;
  background: #ffffff;
  border-left: 1px solid #e2e8f0;
  display: flex;
  flex-direction: column;
  overflow-y: auto;
  position: relative;
  z-index: 1;
}

.order-header {
  padding: 2rem;
  border-bottom: 1px solid #e2e8f0;
  display: flex;
  justify-content: space-between;
  align-items: center;
  background: #f8f9fa;
}

.order-header h3 {
  margin: 0;
  font-size: 1.5rem;
  font-weight: 700;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
}

.order-id {
  font-size: 0.95rem;
  color: #667eea;
  font-weight: 600;
  padding: 0.5rem 1rem;
  background: rgba(102, 126, 234, 0.1);
  border-radius: 10px;
}

.customer-selection {
  padding: 1rem 1.5rem;
  border-bottom: 1px solid #e0e0e0;
}

.customer-selection label {
  display: block;
  font-size: 0.85rem;
  color: #666;
  margin-bottom: 0.5rem;
  font-weight: 500;
}

.customer-select-btn {
  width: 100%;
  padding: 1rem;
  border: 2px solid #e2e8f0;
  border-radius: 12px;
  background: #ffffff;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.5rem;
  font-size: 0.95rem;
  font-weight: 500;
  color: #1a202c;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.05);
}

.customer-select-btn:hover {
  border-color: #667eea;
  background: #ffffff;
  transform: translateY(-2px);
  box-shadow: 0 4px 20px rgba(102, 126, 234, 0.15);
}

.customer-select-btn.has-selection {
  border-color: #667eea;
  background: #f0f4ff;
  color: #667eea;
}

.customer-select-btn .placeholder {
  color: #9e9e9e;
}

.payment-summary {
  padding: 1.5rem;
  border-top: 1px solid #e2e8f0;
  background: #f8f9fa;
}

.payment-summary h4 {
  margin: 0 0 1.25rem 0;
  font-size: 1.2rem;
  font-weight: 700;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
}

.summary-row {
  display: flex;
  justify-content: space-between;
  margin-bottom: 0.75rem;
  font-size: 0.95rem;
  color: #4a5568;
  font-weight: 500;
}

.summary-row.total-row {
  margin-top: 1rem;
  padding-top: 1rem;
  border-top: 2px solid rgba(102, 126, 234, 0.2);
  font-size: 1.3rem;
  font-weight: 700;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
}

.place-order-btn {
  margin: 1.5rem;
  padding: 1.25rem;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: #ffffff;
  border: none;
  border-radius: 16px;
  font-size: 1.1rem;
  font-weight: 700;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.75rem;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  box-shadow: 0 8px 30px rgba(102, 126, 234, 0.4);
  position: relative;
  overflow: hidden;
}

.place-order-btn::before {
  content: "";
  position: absolute;
  top: 0;
  left: -100%;
  width: 100%;
  height: 100%;
  background: linear-gradient(
    90deg,
    transparent,
    rgba(255, 255, 255, 0.3),
    transparent
  );
  transition: left 0.5s;
}

.place-order-btn:hover:not(:disabled)::before {
  left: 100%;
}

.place-order-btn:hover:not(:disabled) {
  transform: translateY(-4px);
  box-shadow: 0 12px 40px rgba(102, 126, 234, 0.5);
}

.place-order-btn:active:not(:disabled) {
  transform: translateY(-2px);
}

.place-order-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
  transform: none;
  box-shadow: 0 4px 15px rgba(102, 126, 234, 0.2);
}

/* Customer Modal */
.customer-modal-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.5);
  backdrop-filter: blur(4px);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1.5rem;
  z-index: 1200;
  animation: fadeIn 0.3s ease;
}

@keyframes fadeIn {
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
}

.customer-modal {
  width: min(600px, 100%);
  max-height: 85vh;
  background: #ffffff;
  border-radius: 28px;
  box-shadow: 0 30px 80px rgba(0, 0, 0, 0.2);
  display: flex;
  flex-direction: column;
  overflow: hidden;
  color: #0f172a;
  border: 1px solid #e2e8f0;
  animation: slideUp 0.4s cubic-bezier(0.4, 0, 0.2, 1);
}

@keyframes slideUp {
  from {
    transform: translateY(30px);
    opacity: 0;
  }
  to {
    transform: translateY(0);
    opacity: 1;
  }
}

.customer-modal-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 1rem;
  padding: 1.75rem 1.75rem 1.25rem;
  border-bottom: 1px solid #e2e8f0;
}

.customer-modal-header h3 {
  margin: 0;
  font-size: 1.4rem;
  font-weight: 700;
  color: #0f172a;
}

.customer-modal-header p {
  margin: 0.35rem 0 0;
  color: #64748b;
  font-size: 0.95rem;
}

.customer-modal-body {
  padding: 1.5rem 1.75rem;
  display: flex;
  flex-direction: column;
  gap: 1.25rem;
  overflow-y: auto;
  flex: 1;
}

.customer-search {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.875rem 1.25rem;
  border-radius: 12px;
  background: #f8fafc;
  border: 2px solid #e2e8f0;
  transition: all 0.2s ease;
}

.customer-search:focus-within {
  border-color: #3b82f6;
  background: white;
  box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
}

.customer-search i {
  color: #94a3b8;
  font-size: 1.1rem;
  flex-shrink: 0;
}

.customer-search input {
  border: none;
  background: transparent;
  outline: none;
  font-size: 0.95rem;
  width: 100%;
  color: #1e293b;
}

.customer-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 0.75rem;
  padding: 3rem 1rem;
  color: #94a3b8;
  text-align: center;
}

.customer-state i {
  font-size: 3rem;
  color: #cbd5e1;
}

.customer-list {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  max-height: 400px;
  overflow-y: auto;
}

.customer-item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  padding: 1rem 1.25rem;
  border-radius: 14px;
  border: 1px solid #e2e8f0;
  background: white;
  cursor: pointer;
  transition: all 0.2s ease;
  text-align: left;
  width: 100%;
}

.customer-item:hover {
  background: linear-gradient(
    135deg,
    rgba(102, 126, 234, 0.1) 0%,
    rgba(118, 75, 162, 0.1) 100%
  );
  border-color: #667eea;
  transform: translateX(6px);
  box-shadow: 0 4px 15px rgba(102, 126, 234, 0.2);
}

.customer-item-content {
  display: flex;
  align-items: center;
  gap: 1rem;
  flex: 1;
  min-width: 0;
}

.customer-avatar {
  width: 48px;
  height: 48px;
  border-radius: 50%;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: #fff;
  font-weight: 700;
  font-size: 1rem;
  display: flex;
  align-items: center;
  justify-content: center;
  text-transform: uppercase;
  flex-shrink: 0;
  box-shadow: 0 4px 15px rgba(102, 126, 234, 0.3);
}

.customer-details {
  display: flex;
  flex-direction: column;
  gap: 0.35rem;
  min-width: 0;
  flex: 1;
}

.customer-details h4 {
  margin: 0;
  font-size: 1rem;
  font-weight: 600;
  color: #0f172a;
  line-height: 1.3;
}

.customer-meta {
  display: flex;
  flex-wrap: wrap;
  gap: 0.75rem;
  font-size: 0.85rem;
  color: #64748b;
}

.meta-item {
  display: flex;
  align-items: center;
  gap: 0.35rem;
}

.meta-item i {
  font-size: 0.9rem;
  color: #94a3b8;
}

.customer-item i:last-child {
  color: #cbd5e1;
  font-size: 1.1rem;
  flex-shrink: 0;
}

.btn {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem;
  border-radius: 8px;
  border: none;
  background: transparent;
  cursor: pointer;
}

.btn-light {
  background: rgba(148, 163, 184, 0.15);
  color: #1f2937;
}

.icon-only {
  padding: 0.5rem;
}

/* Responsive */
@media (max-width: 1024px) {
  .cart-sidebar-fixed {
    width: 320px;
  }

  .pos-main-content {
    margin-left: 320px;
  }

  .menu-items-grid {
    grid-template-columns: repeat(auto-fill, minmax(180px, 1fr));
  }
}

@media (max-width: 768px) {
  .cart-sidebar-fixed {
    width: 100%;
    position: fixed;
    left: -100%;
    transition: left 0.3s ease;
    z-index: 1000;
    box-shadow: 2px 0 20px rgba(0, 0, 0, 0.2);
  }

  .cart-sidebar-fixed.show {
    left: 0;
  }

  .pos-main-content {
    margin-left: 0;
    padding: 1rem;
  }

  .menu-items-grid {
    grid-template-columns: repeat(auto-fill, minmax(140px, 1fr));
    gap: 1rem;
  }

  .search-filter-section {
    flex-direction: column;
  }

  .search-box {
    width: 100%;
  }

  .filter-btn {
    width: 100%;
    justify-content: center;
  }

  .pos-sidebar {
    width: 100%;
    border-left: none;
    border-top: 1px solid #e2e8f0;
  }

  .payment-modal {
    width: 95%;
    max-width: 700px;
  }

  .payment-modal-header,
  .payment-modal-body,
  .payment-modal-footer {
    padding: 1.25rem;
  }
}

/* Payment Modal */
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  backdrop-filter: blur(4px);
}

.payment-modal {
  background: #ffffff;
  border-radius: 24px;
  width: 90%;
  max-width: 700px;
  max-height: 90vh;
  height: auto;
  overflow: hidden;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
  display: flex;
  flex-direction: column;
  animation: modalSlideIn 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

@keyframes modalSlideIn {
  from {
    opacity: 0;
    transform: translateY(-20px) scale(0.95);
  }
  to {
    opacity: 1;
    transform: translateY(0) scale(1);
  }
}

.payment-modal-header {
  padding: 1rem 1.5rem;
  border-bottom: 1px solid #e2e8f0;
  display: flex;
  justify-content: space-between;
  align-items: center;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  flex-shrink: 0;
}

.payment-modal-header h3 {
  margin: 0;
  font-size: 1.25rem;
  font-weight: 700;
  color: #ffffff;
  display: flex;
  align-items: center;
  gap: 0.75rem;
}

.modal-close-btn {
  width: 36px;
  height: 36px;
  border-radius: 8px;
  border: none;
  background: rgba(255, 255, 255, 0.2);
  color: #ffffff;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.25rem;
  transition: all 0.2s;
}

.modal-close-btn:hover {
  background: rgba(255, 255, 255, 0.3);
  transform: rotate(90deg);
}

.payment-modal-body {
  padding: 1.5rem;
  overflow-y: auto;
  flex: 1;
  display: flex;
  flex-direction: column;
}

.payment-info {
  background: #f8f9fa;
  padding: 1rem;
  border-radius: 12px;
  margin-bottom: 1rem;
  flex-shrink: 0;
}

.info-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 0;
}

.info-row:last-child {
  margin-bottom: 0;
}

.info-label {
  font-size: 0.9rem;
  color: #64748b;
  font-weight: 500;
}

.info-value {
  font-size: 1rem;
  color: #1a202c;
  font-weight: 600;
}

.info-value.total-amount {
  font-size: 1.25rem;
  font-weight: 700;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
}

.payment-form {
  display: flex;
  flex-direction: column;
  gap: 1rem;
  flex: 1;
  overflow: hidden;
}

.form-group {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.form-group label {
  font-size: 0.9rem;
  font-weight: 600;
  color: #4a5568;
}

.required {
  color: #ef4444;
}

.form-input,
.form-textarea {
  padding: 0.75rem 1rem;
  border: 2px solid #e2e8f0;
  border-radius: 10px;
  font-size: 1rem;
  transition: all 0.3s;
  font-family: inherit;
}

.form-input:focus,
.form-textarea:focus {
  outline: none;
  border-color: #667eea;
  box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.1);
}

.amount-buttons {
  display: flex;
  gap: 0.5rem;
  flex-wrap: wrap;
  margin-bottom: 0.75rem;
}

.amount-btn {
  padding: 0.5rem 1rem;
  border: 2px solid #e2e8f0;
  border-radius: 8px;
  background: #ffffff;
  color: #4a5568;
  font-size: 0.9rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s;
  flex: 1;
  min-width: 70px;
}

.amount-btn:hover {
  border-color: #667eea;
  background: #f0f4ff;
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(102, 126, 234, 0.2);
}

.amount-btn.active {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: #ffffff;
  border-color: transparent;
  box-shadow: 0 4px 15px rgba(102, 126, 234, 0.4);
  transform: translateY(-2px);
}

.change-display {
  background: linear-gradient(135deg, #10b981 0%, #059669 100%);
  padding: 0.75rem 1rem;
  border-radius: 10px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin: 0.5rem 0;
  animation: slideIn 0.3s ease;
  flex-shrink: 0;
}

@keyframes slideIn {
  from {
    opacity: 0;
    transform: translateY(-10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.change-label {
  font-size: 0.95rem;
  font-weight: 600;
  color: #ffffff;
}

.change-amount {
  font-size: 1.25rem;
  font-weight: 700;
  color: #ffffff;
}

.payment-modal-footer {
  padding: 1rem 1.5rem;
  border-top: 1px solid #e2e8f0;
  display: flex;
  gap: 1rem;
  justify-content: flex-end;
  background: #f8f9fa;
  flex-shrink: 0;
}

.btn-cancel,
.btn-confirm {
  padding: 0.75rem 1.5rem;
  border-radius: 10px;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  transition: all 0.3s;
  border: none;
}

.btn-cancel {
  background: #e2e8f0;
  color: #4a5568;
}

.btn-cancel:hover {
  background: #cbd5e0;
}

.btn-confirm {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: #ffffff;
  box-shadow: 0 4px 15px rgba(102, 126, 234, 0.3);
}

.btn-confirm:hover:not(:disabled) {
  transform: translateY(-2px);
  box-shadow: 0 6px 20px rgba(102, 126, 234, 0.4);
}

.btn-confirm:disabled {
  opacity: 0.6;
  cursor: not-allowed;
  transform: none;
}
</style>
